import { Consent, ErrorResponse, ServiceResponse } from '@bzbs/react-api-client';
import { create } from 'zustand';
import { trackCommonEvents } from '../utils';
import { useBuzzebeesAppStore } from './buzzebeess-app.store';
import { useLocaleStore } from './locale.store';

export type ConsentState = {
  data: Consent | null;
  isLoading: boolean;
  error: ErrorResponse | null;
};

type UpdateConsentParams = {
  termsAndConditions?: string;
  dataPrivacy?: string;
  marketingOption?: string;
  email?: string;
  sms?: string;
  notification?: string;
  line?: string;
  analyticsBuzzebeesCookies?: string;
  analyticsFirebaseCookies?: string;
  analyticsGoogleCookies?: string;
  analyticsMetaCookies?: string;
  analyticsOtherCookies?: string;
  functionalCookies?: string;
  marketingCookies?: string;
  necessaryCookies?: string;
  options?: {
    [key: string]: unknown;
  };
};

export type ConsentActions = {
  fetchData: () => Promise<ServiceResponse<Consent>>;
  updateConsent: (consent: UpdateConsentParams) => Promise<ServiceResponse<Consent>>;
  clear: () => void;
};

export type ConsentStore = ConsentState & ConsentActions;

export const useConsentStore = create<ConsentStore>((set) => {
  return {
    data: null,
    isLoading: false,
    error: null,

    fetchData: async () => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true, error: null });
      const response = await bzbsService.consentApi.consent({
        options: {
          device_locale: useLocaleStore.getState().localeId,
        },
      });
      set({ isLoading: false, error: null });

      if (response.type === 'success') {
        set({ data: response.model });
      } else {
        set({ error: response });
      }

      return response;
    },

    updateConsent: async (consent: UpdateConsentParams) => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true, error: null });
      const response = await bzbsService.consentApi.updateConsent(consent);
      set({ isLoading: false, error: null });

      if (response.type === 'success') {
        set({ data: response.model });
        if (
          response.model.EmailMarketing == 0 &&
          response.model.SMSMarketing == 0 &&
          response.model.NotificationMarketing == 0 &&
          response.model.LineMarketing == 0
        ) {
          trackCommonEvents.unconsentSuccess();
        }
      } else {
        set({ error: response });
      }

      return response;
    },

    clear: () => {
      set({
        data: null,
        isLoading: false,
        error: null,
      });
    },
  };
});
